import { groupBy } from "lodash";
import { MEAL_LIST_ORDER } from "../config/constants";
import { type AllergenType, DishTypeCategory } from "../types";
import { type LocalDishType, MealListSortTypeEnum } from "../types/mainTypes";

/**
 * Filters the available dish types based on the given parameters.
 */
export const filterAvailableDishTypes = (
	fridgeDishTypes: LocalDishType[],
	availableDishTypes: Set<string>,
	allergens: AllergenType[],
	// Required (no default): `true` keeps the SAFE dishes (no filtered allergen) for the main list;
	// `false` keeps the dishes that DO carry a filtered allergen for the "not matching" section. Defaulting
	// it would make a forgotten argument silently surface allergen-containing dishes as the main list.
	exclusive: boolean,
): LocalDishType[] =>
	fridgeDishTypes.filter((dishType) => {
		const hasAllergen = allergens.some((a: AllergenType) => dishType.allergens?.includes(a));

		return exclusive
			? availableDishTypes.has(dishType.id) && !hasAllergen
			: availableDishTypes.has(dishType.id) && hasAllergen;
	});
/**
 * Filters the available dish type IDs based on the given parameters.
 */
export const filterAvailableDishTypeIds = (
	fridgeDishTypes: LocalDishType[],
	availableDishTypes: Set<string>,
	allergens: AllergenType[],
): string[] => {
	const filteredDishTypes = filterAvailableDishTypes(fridgeDishTypes, availableDishTypes, allergens, true);
	return filteredDishTypes.map((dishType) => dishType.id);
};

export const sortAndGroupDishTypesByCategory = (dishTypes: LocalDishType[]) =>
	groupBy(
		dishTypes.sort((a, b) => {
			const categoryA = a.category ?? DishTypeCategory.Meal;
			const categoryB = b.category ?? DishTypeCategory.Meal;

			const orderA = MEAL_LIST_ORDER.categoryOrder[categoryA];
			const orderB = MEAL_LIST_ORDER.categoryOrder[categoryB];

			return orderA - orderB;
		}),
		"category",
	);

function sortDishTypesByName(a: LocalDishType, b: LocalDishType, language: "primary" | "secondary") {
	const descriptionA = a.description?.[language]?.name?.toLowerCase() ?? "";
	const descriptionB = b.description?.[language]?.name?.toLowerCase() ?? "";

	if (MEAL_LIST_ORDER.dishTypeOrderDirection === "DESC") {
		return descriptionA < descriptionB ? 1 : -1;
	}

	return descriptionA < descriptionB ? -1 : 1;
}

function sortDishTypesByPrice(a: LocalDishType, b: LocalDishType) {
	const priceA = Number.parseInt(a.price?.current ?? "0", 10);
	const priceB = Number.parseInt(b.price?.current ?? "0", 10);
	if (MEAL_LIST_ORDER.dishTypeOrderDirection === "DESC") {
		return priceB - priceA;
	}
	return priceA - priceB;
}

export const safeParseNumber = (value: string | undefined | null): number => {
	const parsed = Number.parseFloat(value ?? "");
	return Number.isFinite(parsed) ? parsed : 0;
};

export const getDiscountPercentage = (dishType: LocalDishType): number => {
	const current = safeParseNumber(dishType.price?.current);
	const defaultPrice = safeParseNumber(dishType.price?.default);
	if (defaultPrice <= 0 || current <= 0 || current >= defaultPrice) {
		return 0;
	}
	return Math.round((1 - current / defaultPrice) * 100);
};

export const isDishTypeDiscounted = (dishType: LocalDishType): boolean => getDiscountPercentage(dishType) > 0;

export const sortDishTypes = (dishTypes: LocalDishType[], selectedLanguage: "primary" | "secondary") => {
	const sortedDishTypesByCategory = sortAndGroupDishTypesByCategory(dishTypes);
	return Object.values(sortedDishTypesByCategory).flatMap((item) =>
		item.sort((a: LocalDishType, b: LocalDishType) => {
			const aDiscount = getDiscountPercentage(a);
			const bDiscount = getDiscountPercentage(b);
			const aDiscounted = aDiscount > 0;
			const bDiscounted = bDiscount > 0;
			if (aDiscounted !== bDiscounted) {
				return aDiscounted ? -1 : 1;
			}
			if (aDiscounted && bDiscounted && aDiscount !== bDiscount) {
				return bDiscount - aDiscount;
			}

			switch (MEAL_LIST_ORDER.dishTypeOrder) {
				case MealListSortTypeEnum.NAME:
					return sortDishTypesByName(a, b, selectedLanguage);
				case MealListSortTypeEnum.PRICE: {
					return sortDishTypesByPrice(a, b);
				}
				default: {
					return a.id.localeCompare(b.id);
				}
			}
		}),
	);
};
